import {useState} from 'react'

/**
 *
 * @param keyName
 * @param defaultValue When null and if keyName doesn't exist, it will not be created.
 */
export const useLocalStorage = <T>(
    keyName: string,
    defaultValue: T
): [T, (v: T) => void] => {
    const [storedValue, setStoredValue] = useState((): T => {
        try {
            const value = window.localStorage.getItem(keyName)

            if (value) return JSON.parse(value)

            if (defaultValue !== null)
                window.localStorage.setItem(
                    keyName,
                    JSON.stringify(defaultValue)
                )

            return defaultValue
        } catch (error) {
            console.debug(`Failed to get localStorage key ${keyName}: ${error}`)
            return defaultValue
        }
    })

    /**
     * @param value When null, the localStorage key will be removed.
     */
    const setValue = (value: T) => {
        if (value === null) {
            try {
                window.localStorage.removeItem(keyName)
            } catch (error) {
                console.debug(
                    `Failed to set localStorage key ${keyName}: ${error}`
                )
            }

            return
        }

        let jsonValue = JSON.stringify(value)

        try {
            window.localStorage.setItem(keyName, jsonValue)
            setStoredValue(value)
        } catch (error) {
            console.debug(
                `Failed to set localStorage key ${keyName} to value ${jsonValue}: ${error}`
            )
        }
    }

    return [storedValue, setValue]
}
